/**
 * Pure "active input space for a pointer" resolver — paper→model inside the
 * active VP, identity outside. Extracted from the pointer-input seam
 * (`stageInteractions`) so that file stays under its 2000-line cap; the seam
 * keeps a thin closure that injects the live viewport width / redirect /
 * fallback space id.
 *
 * Single-sources the `paperPpw` derivation (legacy right/left/width axis, so
 * identity behaviour is byte-identical) + the `resolveActiveInputSpace` call
 * shared by `onPointerMove` (snap/hover) and the `onPointerUp` click-commit
 * fallback.
 *
 * `fallbackSpaceId` is the UN-collapsed `identityFallbackSpaceId` (the layout id
 * in MSPACE_VIA_VP), NOT the collapsed dispatch id `activeSpaceId` (== "model"):
 * over paper OUTSIDE the active VP the identity space is scoped to the LAYOUT,
 * keeping visible paper geometry and excluding invisible model geometry. Callers
 * emit `space.spaceId` so hover / commit coords are tagged with the space they
 * actually live in.
 *
 * `ignoreCursorRect` (default `false`) is forwarded to `resolveActiveInputSpace`.
 * When `true` (an in-progress draft, MC4 edge case #2) the cursor-over-rect gate
 * is skipped, so a cursor OUTSIDE the active VP rect still resolves to the VP
 * MODEL space (`spaceId === "model"`) and the rubber band spans past the edge for
 * the clip to trim. `false` keeps the legacy gated pick/snap/hover byte-for-byte.
 */

import * as THREE from "three";

import { resolveActiveInputSpace } from "../viewports/activeInputSpace";
import { MIN_ZOOM } from "../stageCameraMath";

export function resolveInputSpaceForPointer(args: {
  paperPt: { x: number; y: number };
  ortho: THREE.OrthographicCamera;
  ignoreCursorRect?: boolean;
  viewportWidthPx: number;
  redirect: Parameters<typeof resolveActiveInputSpace>[0]["redirect"];
  fallbackSpaceId: string;
}): ReturnType<typeof resolveActiveInputSpace> {
  const paperPpw =
    (Math.max(args.ortho.zoom, MIN_ZOOM) * Math.max(args.viewportWidthPx, 1)) /
    Math.max(args.ortho.right - args.ortho.left, 1e-12);
  return resolveActiveInputSpace({
    paperPt: args.paperPt,
    paperPixelsPerWorld: paperPpw,
    redirect: args.redirect,
    fallbackSpaceId: args.fallbackSpaceId,
    ignoreCursorRect: args.ignoreCursorRect ?? false,
  });
}
